import { __assign, __makeTemplateObject } from "tslib";
/** @jsx jsx */
import { createElement, forwardRef, useCallback, useEffect, useRef, } from 'react';
import { ClassNames, jsx } from '@emotion/core';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { B200, N0, N70A } from '@atlaskit/theme/colors';
import AvatarImage from './AvatarImage';
import { AVATAR_RADIUS, AVATAR_SIZES, BORDER_WIDTH } from './constants';
import { PresenceWrapper } from './Presence';
import { StatusWrapper } from './Status';
import { getButtonProps, getCustomElement, getLinkProps } from './utilities';
import { name as packageName, version as packageVersion } from './version.json';
var getStyles = function (css, _a) {
    var size = _a.size, radius = _a.radius, appearance = _a.appearance, _b = _a.borderColor, borderColor = _b === void 0 ? N0 : _b, stackIndex = _a.stackIndex, isInteractive = _a.isInteractive, isDisabled = _a.isDisabled;
    return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    height: ", "px;\n    width: ", "px;\n    align-items: stretch;\n    background-color: ", ";\n    border-radius: ", ";\n    box-sizing: content-box;\n    cursor: inherit;\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    outline: none;\n    overflow: hidden;\n    position: static;\n    transform: translateZ(0);\n    transition: transform 200ms, opacity 200ms;\n    box-shadow: 0 0 0 ", "px ", ";\n    border: none;\n    margin: ", "px;\n    padding: 0;\n\n    &::-moz-focus-inner {\n      border: 0;\n      margin: 0;\n      padding: 0;\n    }\n\n    &::after {\n      background-color: transparent;\n      bottom: 0px;\n      content: ' ';\n      left: 0px;\n      opacity: 0;\n      pointer-events: none;\n      position: absolute;\n      right: 0px;\n      top: 0px;\n      transition: opacity 200ms;\n      width: 100%;\n    }\n\n    ", "\n\n    ", "\n\n    ", "\n  "], ["\n    height: ", "px;\n    width: ", "px;\n    align-items: stretch;\n    background-color: ", ";\n    border-radius: ", ";\n    box-sizing: content-box;\n    cursor: inherit;\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    outline: none;\n    overflow: hidden;\n    position: static;\n    transform: translateZ(0);\n    transition: transform 200ms, opacity 200ms;\n    box-shadow: 0 0 0 ", "px ", ";\n    border: none;\n    margin: ", "px;\n    padding: 0;\n\n    &::-moz-focus-inner {\n      border: 0;\n      margin: 0;\n      padding: 0;\n    }\n\n    &::after {\n      background-color: transparent;\n      bottom: 0px;\n      content: ' ';\n      left: 0px;\n      opacity: 0;\n      pointer-events: none;\n      position: absolute;\n      right: 0px;\n      top: 0px;\n      transition: opacity 200ms;\n      width: 100%;\n    }\n\n    ", "\n\n    ",
        "\n\n    ",
        "\n  "])), size, size, borderColor, appearance === 'circle' ? '50%' : radius + "px", BORDER_WIDTH, borderColor, BORDER_WIDTH, stackIndex && "position: relative;", isInteractive &&
        "\n      cursor: pointer;\n\n      :focus {\n        outline: none;\n        box-shadow: 0 0 0 " + BORDER_WIDTH + "px " + B200 + "\n      }\n\n      :active,\n      :hover {\n        &::after {\n          background-color: " + N70A + ";\n          opacity: 1;\n        }\n      }\n\n      :active {\n        transform: scale(0.9);\n      }\n    ", isDisabled &&
        "\n        cursor: not-allowed;\n\n        &::after {\n          opacity: 0.7;\n          pointer-events: none;\n          background-color: " + N0 + ";\n        }\n      ");
};
var Avatar = forwardRef(function (_a, ref) {
    var analyticsContext = _a.analyticsContext, _b = _a.appearance, appearance = _b === void 0 ? 'circle' : _b, borderColor = _a.borderColor, children = _a.children, href = _a.href, isDisabled = _a.isDisabled, name = _a.name, onClick = _a.onClick, presence = _a.presence, _c = _a.size, size = _c === void 0 ? 'medium' : _c, src = _a.src, stackIndex = _a.stackIndex, status = _a.status, target = _a.target, testId = _a.testId;
    var createAnalyticsEvent = useAnalyticsEvents().createAnalyticsEvent;
    var customPresenceNode = typeof presence === 'object' ? presence : null;
    var customStatusNode = typeof status === 'object' ? status : null;
    var isValidIconSize = size !== 'xxlarge' && size !== 'xsmall';
    var lastAnalytics = useRef(analyticsContext);
    useEffect(function () {
        lastAnalytics.current = analyticsContext;
    }, [analyticsContext]);
    var getTestId = function (testId, children) {
        return !children
            ? { 'data-testid': testId + "--inner" }
            : { testId: testId + "--inner" };
    };
    var onClickHandler = useCallback(function (event) {
        if (isDisabled || typeof onClick !== 'function') {
            return;
        }
        var analyticsEvent = createAnalyticsEvent({
            action: 'clicked',
            actionSubject: 'avatar',
            attributes: {
                componentName: 'avatar',
                packageName: packageName,
                packageVersion: packageVersion,
            },
        });
        /**
         * To avoid wrapping this component in AnalyticsContext we manually
         * push the parent context's meta data into the context.
         **/
        var context = __assign({ componentName: 'avatar', packageName: packageName,
            packageVersion: packageVersion }, lastAnalytics.current);
        analyticsEvent.context.push(context);
        /** Replicating the logic in the `withAnalyticsEvents` HOC */
        var clone = analyticsEvent.clone();
        if (clone) {
            clone.fire('atlaskit');
        }
        onClick(event, analyticsEvent);
    }, [createAnalyticsEvent, isDisabled, onClick]);
    return (jsx("div", { "data-testid": testId, style: {
            display: 'inline-block',
            position: 'relative',
            outline: 0,
            zIndex: stackIndex,
        } },
        jsx(ClassNames, null, function (_a) {
            var css = _a.css;
            var props = __assign(__assign(__assign(__assign({ className: getStyles(css, {
                    size: AVATAR_SIZES[size],
                    radius: AVATAR_RADIUS[size],
                    appearance: appearance,
                    borderColor: borderColor,
                    stackIndex: stackIndex,
                    isInteractive: Boolean(href || onClick),
                    isDisabled: isDisabled,
                }) }, (href && getLinkProps(href, target))), (!href &&
                onClick &&
                getButtonProps(onClickHandler, isDisabled))), (testId && getTestId(testId, children))), { children: (jsx(AvatarImage, { alt: name, appearance: appearance, size: size, src: src })), ref: ref });
            return children
                ? children(props)
                : createElement(getCustomElement(href, onClick), props);
        }),
        isValidIconSize && presence && !status && (jsx(PresenceWrapper, { appearance: appearance, size: size, presence: !customPresenceNode && presence, testId: testId }, customPresenceNode)),
        isValidIconSize && status && (jsx(StatusWrapper, { appearance: appearance, size: size, borderColor: borderColor, status: !customStatusNode && status, testId: testId }, customStatusNode))));
});
Avatar.displayName = 'Avatar';
export default Avatar;
var templateObject_1;
//# sourceMappingURL=Avatar.js.map